import React, { useEffect, useState, useMemo, useContext, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import API from "../api/api";
import { useNavigate } from "react-router-dom";

import {
  FiPlus,
  FiTrash2,
  FiMenu,
  FiX,
  FiLogOut,
  FiUser,
  FiSettings,
  FiSearch,
  FiEdit3,
} from "react-icons/fi";

import "../style/sidebar.css";
import AccountSwitcherModal from "./AccountSwitcherModal";
import ChatSearchModal from "./ChatSearchModal";
import DeleteChatModal from "./DeleteChatModal";
import SettingsPopup from "./SettingsPopup";
import HelpModal from "./HelpModal";
import LogoutConfirmModal from "./LogoutConfirmModal";

import {
  setMessages,
  setActiveChatId,
  clearActiveChat,
  setChatList,
  removeChatFromList,
  setSidebarCollapsed,
} from "../store/chatSlice";

import { clearUser, setCredits } from "../store/userSlice";
import { ThemeContext } from "../context/ThemeContext";
import { PanelLeftCloseIcon, PanelLeftOpenIcon, ChevronDown, ChevronRight, MessageCircle } from "lucide-react";
import { getTranslation } from "../utils/language";
import { useTransliteratedName } from "../utils/transliterate";

const getSidebarText = (language, key) => getTranslation(language, "sidebar", key);


export default function Sidebar() {
  const dispatch   = useDispatch();
  const navigate   = useNavigate();

  const user         = useSelector((state) => state.user.user);
  const activeChatId = useSelector((state) => state.chat.activeChatId);
  const chatsStore   = useSelector((state) => state.chat.chats);
  const chatList     = useSelector((state) => state.chat.chatList);
  const credits      = useSelector((state) => state.user.credits);

  const collapsed = useSelector((state) => state.chat.sidebarCollapsed);
  const [loading,                  setLoading]                  = useState(true);
  const [userMenuOpen,             setUserMenuOpen]             = useState(false);
  const [settingsPopupOpen,        setSettingsPopupOpen]        = useState(false);
  const [helpModalOpen,            setHelpModalOpen]            = useState(false);
  const [accountModalOpen,         setAccountModalOpen]         = useState(false);
  const [searchModalOpen,          setSearchModalOpen]          = useState(false);
  const [deleteModalOpen,          setDeleteModalOpen]          = useState(false);
  const [logoutModalOpen,          setLogoutModalOpen]          = useState(false);
  const [chatIdToDelete,           setChatIdToDelete]           = useState(null);
  const [historyOpen,              setHistoryOpen]              = useState(true);
  const [uiLanguage,               setUiLanguage]               = useState(() => localStorage.getItem("grgpt_ui_language") || "en");
  const [userPlanName,             setUserPlanName]             = useState("Basic");
  const transliteratedUserName = useTransliteratedName(user?.name, uiLanguage);

  const { theme, toggleTheme } = useContext(ThemeContext);
  const menuRef = useRef(null);

  useEffect(() => {
    if (user) {
      API.get("/subscription/my-subscription")
        .then((res) => {
          if (res.data?.planName) {
            const pName = res.data.planName;
            const lower = pName.toLowerCase();
            if (lower.includes("enterprise") || lower === "pro" || /\bpro\b/.test(lower)) {
              setUserPlanName("Pro");
            } else if (lower.includes("professional") || lower.includes("plus") || lower.includes("govtech")) {
              setUserPlanName("Plus");
            } else if (lower.includes("citizen") || lower.includes("basic")) {
              setUserPlanName("Basic");
            } else {
              setUserPlanName(pName);
            }
          } else {
            setUserPlanName("Basic");
          }
        })
        .catch(() => setUserPlanName("Basic"));
    } else {
      setUserPlanName("Basic");
    }
  }, [user]);

  // ── Close user menu on outside click ─────────────────────────────────────
  useEffect(() => {
    const handleOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, []);

  // ── Load data ─────────────────────────────────────────────────────────────
  useEffect(() => {
    const syncLanguage = (e) => setUiLanguage(e.detail || localStorage.getItem("grgpt_ui_language") || "en");
    window.addEventListener("grgpt-language-change", syncLanguage);
    return () => window.removeEventListener("grgpt-language-change", syncLanguage);
  }, []);

  useEffect(() => {
    if (user) {
      loadChats();
      loadCredits();
    }
  }, [user]);

  useEffect(() => {
    if (!user) {
      dispatch(setChatList([]));
      dispatch(setCredits(null));
      setLoading(false); // ✅ Fix: set loading to false for guest users
    }
  }, [user, dispatch]);

  const loadChats = async () => {
    try {
      const res = await API.get("/chat");
      dispatch(setChatList(res.data || []));
    } catch (err) {
      console.error("Failed to load chats", err);
    } finally {
      setLoading(false);
    }
  };

  const loadCredits = async () => {
    try {
      const res = await API.get("/user/credits");
      dispatch(setCredits(res.data?.credits ?? 0));
    } catch (err) {
      console.error("Failed to load credits", err);
    }
  };

  // ── Open chat ─────────────────────────────────────────────────────────────
  const openChat = async (chatId) => {
    dispatch(setActiveChatId(chatId));
    navigate("/");
    if (window.innerWidth <= 1024) {
      dispatch(setSidebarCollapsed(true));
    }
    if (chatsStore[chatId]?.messages?.length > 0) return;
    try {
      const res = await API.get(`/chat/${chatId}`);
      dispatch(setMessages({ chatId, messages: res.data.messages || [] }));
    } catch (err) {
      console.error("Open chat error", err);
    }
  };

  // ── Delete chat ───────────────────────────────────────────────────────────
  const deleteChat = async (chatId) => {
    try {
      await API.delete(`/chat/${chatId}`);
      dispatch(removeChatFromList(chatId));
      if (chatId === activeChatId) dispatch(clearActiveChat());
    } catch (err) {
      console.error("Delete chat error", err);
    }
  };

  const handleRequestDelete = (e, chatId) => {
    e.stopPropagation();
    setChatIdToDelete(chatId);
    setDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (chatIdToDelete) {
      await deleteChat(chatIdToDelete);
      setChatIdToDelete(null);
    }
  };

  // ── New chat ──────────────────────────────────────────────────────────────
  const handleNewChat = () => {
    dispatch(clearActiveChat());
    navigate("/");
    if (window.innerWidth <= 1024) {
      dispatch(setSidebarCollapsed(true));
    }
  };

  // ── Logout ────────────────────────────────────────────────────────────────
  const handleLogoutRequest = () => {
    setUserMenuOpen(false);
    setLogoutModalOpen(true);
  };

  const handleConfirmLogout = async () => {
    setLogoutModalOpen(false);
    setUserMenuOpen(false);
    setSettingsPopupOpen(false);
    localStorage.removeItem("grgpt_token");
    localStorage.removeItem("activeChatId");
    localStorage.removeItem("grgpt_guest_id");
    if (window.innerWidth <= 1024) {
      dispatch(setSidebarCollapsed(true));
    }
    await API.post("/auth/logout").catch(() => {});
    dispatch(clearUser());
    dispatch(clearActiveChat());
    navigate("/login", { replace: true });
  };

  // ── Account ───────────────────────────────────────────────────────────────
  const handleAccount = () => {
    setUserMenuOpen(false);
    navigate("/account");
    if (window.innerWidth <= 1024) {
      dispatch(setSidebarCollapsed(true));
    }
  };

  // ── Sort chats ────────────────────────────────────────────────────────────
  const sortedChats = useMemo(() => {
    return [...chatList].sort(
      (a, b) => new Date(b.updatedAt) - new Date(a.updatedAt)
    );
  }, [chatList]);


  return (
    <aside className={`sidebar ${collapsed ? "collapsed" : ""}`}>
      {/* ── Collapsed Vertical Icon Bar (Gemini style on desktop) ── */}
      <div className="sidebar-collapsed-nav">
        {/* Top Actions: Logo (morphs into Toggle on hover) -> New Chat -> Search */}
        <div className="collapsed-top-actions">
          {/* 1. Logo at the top (morphs into sidebar toggle on hover) */}
          <div className="collapsed-btn-wrapper">
            <button
              type="button"
              className="collapsed-icon-btn collapsed-logo-toggle-btn"
              onClick={() => dispatch(setSidebarCollapsed(false))}
              aria-label="Open sidebar"
            >
              <img src="/grgpt-emblem.png" alt="GRGPT" className="collapsed-logo-static" />
              <span className="collapsed-toggle-hover-icon">
                <PanelLeftOpenIcon size={18} />
              </span>
            </button>
            <span className="sidebar-tooltip">Open sidebar</span>
          </div>

          {/* 2. New Chat */}
          <div className="collapsed-btn-wrapper">
            <button
              type="button"
              className="collapsed-icon-btn collapsed-new-chat-btn"
              onClick={handleNewChat}
              aria-label="New chat"
            >
              <FiEdit3 size={18} />
            </button>
            <span className="sidebar-tooltip">{getSidebarText(uiLanguage, "newChat")}</span>
          </div>

          {/* 3. Search */}
          <div className="collapsed-btn-wrapper">
            <button
              type="button"
              className="collapsed-icon-btn collapsed-search-btn"
              onClick={() => setSearchModalOpen(true)}
              aria-label="Search"
            >
              <FiSearch size={18} />
            </button>
            <span className="sidebar-tooltip">Search</span>
          </div>

          {/* 4. Chats */}
          <div className="collapsed-btn-wrapper">
            <button
              type="button"
              className="collapsed-icon-btn collapsed-chats-btn"
              onClick={() => dispatch(setSidebarCollapsed(false))}
              aria-label="Chats"
            >
              <MessageCircle size={18} />
            </button>
            <span className="sidebar-tooltip">Chats</span>
          </div>
        </div>

        {/* Spacer */}
        <div className="collapsed-nav-spacer" />

        {/* Bottom Actions: Settings -> Profile Photo */}
        <div className="collapsed-bottom-actions">
          {/* Settings Icon */}
          <div className={`collapsed-btn-wrapper ${settingsPopupOpen ? "popup-open" : ""}`}>
            <button
              type="button"
              className={`collapsed-icon-btn collapsed-settings-btn ${settingsPopupOpen ? "active" : ""}`}
              onClick={(e) => {
                e.stopPropagation();
                e.currentTarget.blur();
                setSettingsPopupOpen((prev) => !prev);
              }}
              aria-label="Settings"
            >
              <FiSettings size={18} />
            </button>
            {!settingsPopupOpen && (
              <span className="sidebar-tooltip">Settings</span>
            )}
          </div>

          {/* Profile Avatar */}
          <div className="collapsed-btn-wrapper">
            <button
              type="button"
              className="collapsed-icon-btn collapsed-avatar-btn"
              onClick={() => (user ? setAccountModalOpen(true) : navigate("/login"))}
              aria-label={user?.name || "Account"}
            >
              {user?.profilePicture || user?.avatar ? (
                <img
                  src={user.profilePicture || user.avatar}
                  alt="Profile"
                  className="collapsed-avatar-img"
                  referrerPolicy="no-referrer"
                />
              ) : (
                <div className="collapsed-avatar-initial">
                  {user?.name?.[0]?.toUpperCase() || <FiUser size={15} />}
                </div>
              )}
            </button>
            <span className="sidebar-tooltip">
              {user ? (transliteratedUserName || user?.name) : "Sign In"}
            </span>
          </div>
        </div>
      </div>

      {/* ── Expanded Full Sidebar (Drawer on mobile/tablet, full sidebar on desktop) ── */}
      <div className="sidebar-expanded-content">
        {/* ── Top bar ── */}
        <div className="sidebar-top">
          <div className="sidebar-brand">
            <img src="/grgpt-emblem.png" alt="GRGPT Emblem" className="sidebar-logo-img" />
            <h2 className="logo-text">GRGPT</h2>
          </div>
          <div className="collapse-btn-wrapper">
            <button
              type="button"
              className="collapse-btn"
              onClick={() => dispatch(setSidebarCollapsed(true))}
              aria-label="Close sidebar"
            >
              <PanelLeftCloseIcon size={17} />
            </button>
            <span className="sidebar-tooltip">Close sidebar</span>
          </div>
        </div>

        {/* ── Primary Action Buttons (New Chat & Search) ── */}
        <div className="sidebar-primary-actions">
          <button
            type="button"
            className="sidebar-action-row-btn new-chat-pill-btn"
            onClick={handleNewChat}
          >
            <FiEdit3 size={16} />
            <span>{getSidebarText(uiLanguage, "newChat")}</span>
          </button>

          <button
            type="button"
            className="sidebar-action-row-btn search-chats-pill-btn"
            onClick={() => setSearchModalOpen(true)}
          >
            <FiSearch size={16} />
            <span>Search chats</span>
          </button>
        </div>

        {/* ── Chat history ── */}
        <div className="chat-history">
          <button
            type="button"
            className="chat-history-toggle-btn"
            onClick={() => setHistoryOpen((prev) => !prev)}
            aria-expanded={historyOpen}
          >
            <span>Chats</span>
            {historyOpen ? (
              <ChevronDown size={14} className="history-chevron-icon" />
            ) : (
              <ChevronRight size={14} className="history-chevron-icon" />
            )}
          </button>

          {historyOpen && (
            <div className="chat-history-list">
              {loading && <p className="loading-text">{getSidebarText(uiLanguage, "loading")}</p>}

              {!loading && sortedChats.length === 0 && (
                <p className="no-chat-text">{user ? getSidebarText(uiLanguage, "noChats") : getSidebarText(uiLanguage, "signInHistory")}</p>
              )}

              {!loading && sortedChats.map((chat) => (
                <div
                  key={chat._id}
                  className={`chat-item ${activeChatId === chat._id ? "chat-item-active" : ""}`}
                >
                  <div className="chat-main" onClick={() => openChat(chat._id)}>
                    <span>{chat.title || getSidebarText(uiLanguage, "untitled")}</span>
                  </div>
                  <button className="delete-btn" onClick={(e) => handleRequestDelete(e, chat._id)} title="Delete chat">
                    <FiTrash2 />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* ── Footer ── */}
        <div className="sidebar-footer">
          {user ? (
            /* ── Logged-in: avatar/name click → Account Switcher Modal ── */
            <div className="user-menu-wrapper" ref={menuRef}>
              <button
                className="user-info user-info-btn"
                onClick={() => setAccountModalOpen(true)}
                aria-haspopup="true"
                aria-expanded={accountModalOpen}
              >
                <div className="avatar avatar-silver">
                  {user?.profilePicture || user?.avatar ? (
                    <img
                      src={user.profilePicture || user.avatar}
                      alt="Profile"
                      className="user-avatar-img"
                      referrerPolicy="no-referrer"
                    />
                  ) : (
                    user?.name?.[0]?.toUpperCase()
                  )}
                </div>
                <div className="user-text">
                  <div className="user-name">{transliteratedUserName || user?.name}</div>
                  <div className="user-role">{user?.role === "admin" ? "Admin" : userPlanName}</div>
                </div>
                <div
                  className={`settings-icon-wrapper ${settingsPopupOpen ? "popup-open" : ""}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    setSettingsPopupOpen((prev) => !prev);
                  }}
                >
                  <FiSettings className="user-settings-icon" size={17} />
                  {!settingsPopupOpen && (
                    <span className="settings-tooltip">Settings</span>
                  )}
                </div>
              </button>
            </div>
          ) : (
            /* ── Guest: login / signup ── */
            <div className="auth-buttons-wrapper">
              <div className="auth-buttons" style={{ marginTop: 0 }}>
                <button className="login-btn" onClick={() => {
                  if (window.innerWidth <= 1024) dispatch(setSidebarCollapsed(true));
                  navigate("/login");
                }}>
                  Login
                </button>
                
                <button className="signup-btn" onClick={() => {
                  if (window.innerWidth <= 1024) dispatch(setSidebarCollapsed(true));
                  navigate("/signup");
                }}>
                  Sign Up
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── Settings Options Dropdown Popup ── */}
      <SettingsPopup
        isOpen={settingsPopupOpen}
        onClose={() => setSettingsPopupOpen(false)}
        user={user}
        userPlanName={userPlanName}
        transliteratedUserName={transliteratedUserName}
        onLogout={handleLogoutRequest}
        onOpenHelp={() => setHelpModalOpen(true)}
        collapsed={collapsed}
      />

      {/* ── Help & Support Modal ── */}
      <HelpModal
        isOpen={helpModalOpen}
        onClose={() => setHelpModalOpen(false)}
      />

      {/* ── Google Account Switcher Modal Card ── */}
      <AccountSwitcherModal
        isOpen={accountModalOpen}
        onClose={() => setAccountModalOpen(false)}
      />

      {/* ── Chat Search Popup Modal ── */}
      <ChatSearchModal
        isOpen={searchModalOpen}
        onClose={() => setSearchModalOpen(false)}
        chats={sortedChats}
        activeChatId={activeChatId}
        onSelectChat={openChat}
        onNewChat={handleNewChat}
      />

      {/* ── Delete Chat Confirmation Modal ── */}
      <DeleteChatModal
        isOpen={deleteModalOpen}
        onClose={() => {
          setDeleteModalOpen(false);
          setChatIdToDelete(null);
        }}
        onConfirm={handleConfirmDelete}
      />

      {/* ── Logout Confirmation Modal ── */}
      <LogoutConfirmModal
        isOpen={logoutModalOpen}
        onClose={() => setLogoutModalOpen(false)}
        onConfirm={handleConfirmLogout}
      />

    </aside>
  );
}
